// Deal-logging API layer.
//
// Backend: `salesagent-dashboard-backend`. Same auth model as the rest of the
// app — every request goes through the shared `createApiClient`, which attaches
// the Firebase ID token and the `?user=<dealerUid>` scoping param used for
// staff/admin impersonation.
//
// `POST /deal` records the commercial side of a sale (price, gross, salesperson,
// deal type, trade-in) for a VIN + conversation. It is deliberately NOT gated on
// the `reviewMax` feature flag: every dealership logs its sales, only the review
// request that can follow one is a Review Max feature.
import { post } from "../setting/actions";

export const DASHBOARD_FN = "salesagent-dashboard-backend";

/** The deal types the backend accepts, in the order they're offered. */
export const DEAL_TYPES = ["Finance", "Cash", "Lease"] as const;

export type DealType = (typeof DEAL_TYPES)[number];

export interface DealPayload {
  VINNumber: string;
  /** Conversation the sale belongs to. Empty for a brand-new walk-in customer. */
  convID: string;
  name: string;
  phone: string;
  email: string;
  /** Money fields are sent as raw strings — the backend parses them. */
  salePrice: string;
  gross: string;
  /** `YYYY-MM-DD` in the dealer's local time. */
  saleDate: string;
  /** Epoch milliseconds, stringified, for exact ordering. */
  saleDateTs: string;
  dealType: string;
  tradeIn: string;
  /** Staff member's uid, or "" when unassigned. */
  salesPerson: string;
}

/** `YYYY-MM-DD` for the given date (local), matching the backend's saleDate string. */
export const dateToISODate = (d: Date): string => {
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/** `POST salesagent-dashboard-backend/deal` — log the sale. */
export const createDeal = <T = any>(payload: DealPayload): Promise<T> =>
  post<T>(payload, `${DASHBOARD_FN}/deal`);
